// Correct Firebase imports to use package names, not node_modules paths
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { initializeFirestore, persistentLocalCache } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Firebase config: MUST use environment variables - no fallback keys (security critical)
// Required env vars: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, etc.
const getRequiredEnv = (key) => {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}. Check your .env file.`);
  }
  return value;
};

const firebaseConfig = {
  apiKey: getRequiredEnv('VITE_FIREBASE_API_KEY'),
  authDomain: getRequiredEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  databaseURL: getRequiredEnv('VITE_FIREBASE_DATABASE_URL'),
  projectId: getRequiredEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getRequiredEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getRequiredEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getRequiredEnv('VITE_FIREBASE_APP_ID'),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID // Optional analytics
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with persistent local cache (recommended way)
const db = initializeFirestore(app, {
  localCache: persistentLocalCache()
});

// Initialize Firebase Auth
const auth = getAuth(app);

// Only initialize analytics and log in development
let analytics;
const isDev = window?.location?.hostname === 'localhost' || window?.location?.hostname === '127.0.0.1';

function enableAnalytics() {
  if (!isDev && window.location.protocol === 'https:' && firebaseConfig.measurementId) {
    try {
      if (!analytics) analytics = getAnalytics(app);
    } catch (e) {
      // Silently ignore analytics errors in unsupported environments
    }
  }
}

// App Check: Use ReCaptchaV3Provider for both dev and prod, set debug token in dev
let appCheck;
if (isDev) {
  if (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
  }
}

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (!recaptchaSiteKey) {
  console.error('Missing VITE_RECAPTCHA_SITE_KEY - App Check will be initialized without reCAPTCHA');
}
if (!isDev && !recaptchaSiteKey) {
  throw new Error('VITE_RECAPTCHA_SITE_KEY is required for production');
}

if (recaptchaSiteKey) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Export only what is needed, avoid analytics in production
export { app, db, auth, appCheck, enableAnalytics };
